import AuthUser from '../models/auth_user'
import { Location } from '@/models'
import camelCase from "lodash-es/camelCase"
import configurations from '../config'
// Util function to mount and unmount the appopintment modal based on the type
import firebase from 'firebase/compat/app'
import forEach from "lodash-es/forEach"
import isArray from "lodash-es/isArray"
import isObject from "lodash-es/isObject"
import { loadRemote } from '@module-federation/runtime'
import store from '../store'
import { useAppContext } from '../pmd/composition/useAppContext'
import { Scope, useScopedStore } from '../plugins/setupMicroAppStore'

let bookAppointmentApp: any = {}
let viewAppointmentApp: any = {}
let calendarPageApp : any = {}
let appointmentListApp : any = {}

function safeUnmount(appInstance: any) {
  try {
    if (!appInstance) {
      return
    }
    if (typeof appInstance.unmount === 'function') {
      appInstance.unmount()
    }
  } catch (error) {
    console.error('Error unmounting calendar app:', error)
  }
}

// Function to mount a modal component
async function mountAppointmentModal(mountId: string, configuration: any) {
  // Unmount any existing instance of the modal
  safeUnmount(bookAppointmentApp)
  safeUnmount(viewAppointmentApp)
  safeUnmount(calendarPageApp)
  safeUnmount(appointmentListApp)

  const { mountAppContext } = useAppContext()
  const showModal = true
  let Component
  switch (mountId) {
    case 'BookAppointment':
      Component = "BookAppointment"
      break
    case 'ViewAppointment':
      Component = "ViewAppointment"
      break
    case 'CalendarPage':
      Component = "CalendarPage"
      break
    case 'AppointmentList':
      Component = "AppointmentList"
      break
    default:
      Component = null
  }

  try{
    const [createApp, calendarComponent] = await Promise.all([
      loadRemote('appointmentModalApp/vue3').then((m: any) => m.createApp),
      loadRemote('appointmentModalApp/'+Component).then((m: any) => m.default),
    ])

    const appointmentModalApp = createApp(calendarComponent, {
      ...configuration,
      showModal: showModal, // Adding the showModal flag to the configuration
    })

    mountAppContext({ appointmentModalApp })
    appointmentModalApp.mount(`#${mountId}`)

    // Store the app instance in the corresponding variable
    if (mountId === 'BookAppointment') {
      bookAppointmentApp = appointmentModalApp
    } else if (mountId === 'ViewAppointment') {
      viewAppointmentApp = appointmentModalApp
    } else if (mountId === 'CalendarPage') {
      calendarPageApp = appointmentModalApp
    } else if (mountId === 'AppointmentList') {
      appointmentListApp = appointmentModalApp
    }
  }
  catch(e){
    console.log(e)
  }


}

// Function to unmount a modal component
function unmountAppointmentModal(mountId: string) {
let appToUnmount;

switch(mountId) {
  case 'BookAppointment':
    appToUnmount = bookAppointmentApp;
    break;
  case 'ViewAppointment':
    appToUnmount = viewAppointmentApp;
    break;
  case 'CalendarPage':
    appToUnmount = calendarPageApp;
    break;
  case 'AppointmentList':
    appToUnmount = appointmentListApp;
    break;
}
  if (appToUnmount?.unmount) {
    try {
      const mountElement = document.getElementById(mountId)
      if (mountElement) {
        appToUnmount.unmount()
      }
    } catch (error) {
    } finally {
      window['appointmentModalApp'] = null
    }
  }
}

function camelize(obj, keysToExclude: string[] = []) {
  if (isObject(obj)) {
    if (isArray(obj)) {
      return obj.map(item => camelize(item))
    } else {
      const newObj = {}
      forEach(obj, (value, key) => {
        const camelKey = camelCase(key)
        if (keysToExclude.includes(key)) {
          newObj[camelKey] = value
        } else {
          newObj[camelKey] = camelize(value, keysToExclude)
        }
      })
      return newObj
    }
  }
  return obj
}

async function getCalendarData(locationId: string) {
  const location = await new Location(
    await store.dispatch('locations/getCurrentLocation', locationId)
  )
  const tokenId = await firebase.auth().currentUser?.getIdToken()
  const auth = new AuthUser(await store.dispatch('auth/get'))
  const userId = firebase.auth().currentUser?.uid || store.state.user.id
  const userType = store.state.user?.user?.type
  const userRole = store.state.user?.user?.role
  await store.dispatch('calendars/syncAll', locationId)
  await store.dispatch('calendars/syncCalendarServices', locationId)
  const calendars = store.state.calendars.calendars
  const filteredCalendars = calendars.map((calendar: any) => camelize(calendar))
  const groups = store.state.teams.teams
  const filteredGroups = groups.map((group: any) => camelize(group))
  const users = store.state.users.users
  const fileteredUsers = users.map((user: any) => camelize(user))
  const loggedInUserInfo = camelize(store.getters['user/get'], ['location_wise_timezone', 'user_calendar','locations'])
  await store.dispatch('calendars/syncUserTimezonePreference', { locationId, userId: loggedInUserInfo?.id })
  const calendarsConfig = store.getters['calendars/getSubAccountConfig'];
  let whitelabelUrl = ''
  if (!whitelabelUrl) {
    whitelabelUrl = window.location.origin
  }

  const scope: Scope = {
    state:  ['users', 'user', 'locations'],
    dispatch: ['locationCustomFields/syncAll', 'locationCustomFields/getCustomFields', 'agencyUsers/syncGet'],
    getters: ['locations/getCurrentLocation', 'locations/getCurrentLocationTimeZone', 'featureFlags/getForLocation', 'customObjectsStore/getObjectSingularLabelByKey', 'customObjectsStore/getObjectPluralLabelByKey','customObjectsStore/getAllCustomObjectSchemas', 'pipelines/getById']
  }

  const data = {
    name: 'calendar-app',
    locationId: location._id,
    startDayOfWeek: Number.isNaN(location.appStartDayOfWeek) ? 0 : Number(location.appStartDayOfWeek),
    tokenId: tokenId,
    disableContactTimezone: location.disableContactTimezone,
    authApiKey: auth.apiKey,
    userId: userId,
    userType: userType,
    userRole: userRole,
    timeZone: location.timezone,
    locale:
      store.state.user.user.platformLanguage ||
      location.localeSettings.platform,
    company: {
      plan: store.state.company?.company?.stripe_active_plan,
      id: store.state.company?.company?.id,
    },
    whitelabelUrl: whitelabelUrl,
    recurringEventsEnabled: true,
    calendars: filteredCalendars,
    groups: filteredGroups,
    users: fileteredUsers,
    loggedInUserInfo: loggedInUserInfo,
    locationCountry:location?.country,
    // isTimezoneConfigEnabled: store.getters["featureFlags/getForLocation"]("calendars.timezoneConfig"),
    isTimezoneConfigEnabled: true,
    isHidePrivateAppointmentsEnabled: store.getters["featureFlags/getForLocation"]("calendars.hidePrivateAppointments"),
    isAppointmentNotesEnabled: store.getters['LevelUpDayFlag/isfeatursActive'],
    isCustomRecurringEventsEnabled: !!(store.getters['LevelUpDayFlag/isfeatursActive'] && store.getters["featureFlags/getForLocation"]("calendars.customRecurring")),
    calendarSubAccountConfig: calendarsConfig,
    isServicesEnabled: store.getters['calendars/isServicesEnabled'],
    services: store.getters['calendars/getAllServices'],
    userTimezonePreference: getEffectiveTimezone(location.timezone),
    customObjectLabels: {
      contact: {
        singular: store.getters['customObjectsStore/getObjectSingularLabelByKey']('contact'),
        plural: store.getters['customObjectsStore/getObjectPluralLabelByKey']('contact')
      }
    },
    store: useScopedStore(scope)
  }
  return data
}

function getEffectiveTimezone(businessTimezone: string): string {
  const userTimezonePreference = store.getters['calendars/getUserTimezonePreference']

  // If user preference is 'business_timezone', use the business timezone
  // Otherwise, use the user's preferred timezone or fall back to business timezone
  return userTimezonePreference?.value === 'business_timezone'
      ? businessTimezone
      : userTimezonePreference?.value || businessTimezone
}

export { mountAppointmentModal, unmountAppointmentModal, getCalendarData, camelize, getEffectiveTimezone }
